import * as FileSaver from 'file-saver';
import * as XLSX from 'xlsx';

const BROWSERS = Object.freeze({
    SAFARI: "safari",
    EDGE: "edge",
    OTHER: "other"
});

export default class ExcelWrapper {
    static DEFAULT_FILE_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    static DEFAULT_BOOK_TYPE = 'xlsx';

    static normalizeFileName(fileName) {
        return fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
    }

    static async saveFile(blob, fileName) {
        const browser = ExcelWrapper.checkBrowser();

        if (browser === BROWSERS.EDGE) {
            await window.navigator.msSaveBlob(blob, fileName);
        } else if (browser === BROWSERS.SAFARI) {
            const link = document.createElement('a');
            const url = window.URL.createObjectURL(blob);
            link.href = url;
            link.download = fileName;

            document.body.appendChild(link);
            link.click();

            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } else {
            await FileSaver.saveAs(blob, fileName);
        }
    }

    static createExcelBlob(wb, options = {}) {
        const fileType = options.fileType || this.DEFAULT_FILE_TYPE;
        const bookType = options.bookType || this.DEFAULT_BOOK_TYPE;

        const excelBuffer = XLSX.write(wb, {
            bookType,
            type: 'array',
            cellStyles: options.cellStyles
        });

        return new Blob([excelBuffer], { type: fileType });
    }

    static autoFitColumns(ws, data) {
        const objectMaxLength = [];

        // Get header row
        const headers = Object.keys(data[0]);

        // Get max length for each column including header
        headers.forEach((header, i) => {
            objectMaxLength[i] = header.length;

            data.forEach(row => {
                const value = row[header]?.toString() || '';
                if (value.length > objectMaxLength[i]) {
                    objectMaxLength[i] = value.length;
                }
            });
        });

        ws['!cols'] = objectMaxLength.map(length => ({
            wch: length + 2  
        }));
    }

    static async write(fileName, sheetName, data, options = {}) {
        try {
            if (!fileName) throw new Error('fileName is required');
            if (!sheetName) throw new Error('sheetName is required');
            if (!Array.isArray(data)) throw new Error('data must be an array');
            if (data.length === 0) throw new Error('data array cannot be empty');

            const finalFileName = this.normalizeFileName(fileName);

            const ws = XLSX.utils.json_to_sheet(data, {
                dateNF: options.dateNF,
                cellStyles: options.cellStyles
            });

            // Auto-fit columns if enabled
            if (options.autoWidth) {
                this.autoFitColumns(ws, data);
            }

            const wb = {
                Sheets: { [sheetName]: ws },
                SheetNames: [sheetName]
            };

            const blob = this.createExcelBlob(wb, options);
            await this.saveFile(blob, finalFileName);

            return true;
        } catch (error) {
            console.error('Excel generation failed:', error);
            throw new Error(`Failed to generate Excel file: ${error.message}`);
        }
    }

    static async writeMultipleSheets(fileName, sheets, options = {}) {
        try {
            if (!sheets || !Array.isArray(sheets) || sheets.length === 0) {
                throw new Error('sheets array is required and cannot be empty');
            }

            const finalFileName = this.normalizeFileName(fileName);
            const wb = { Sheets: {}, SheetNames: [] };

            sheets.forEach(sheet => {
                if (!sheet.name || !Array.isArray(sheet.data)) {
                    throw new Error('Each sheet must have a name and valid data array');
                }

                const ws = XLSX.utils.json_to_sheet(sheet.data, {
                    dateNF: options.dateNF,
                    cellStyles: options.cellStyles
                });

                // Auto-fit columns if enabled
                if (options.autoWidth) {
                    this.autoFitColumns(ws, sheet.data);
                }

                wb.Sheets[sheet.name] = ws;
                wb.SheetNames.push(sheet.name);
            });

            const blob = this.createExcelBlob(wb, options);
            await this.saveFile(blob, finalFileName);

            return true;
        } catch (error) {
            console.error('Excel generation failed:', error);
            throw new Error(`Failed to generate multi-sheet Excel file: ${error.message}`);
        }
    }

    static checkBrowser() {
        const ua = window.navigator.userAgent;
        if (ua.indexOf('Edge') > -1)
            return BROWSERS.EDGE;
        else if (/^((?!chrome|android).)*safari/i.test(ua))
            return BROWSERS.SAFARI;
        return BROWSERS.OTHER;
    }
}